import { defineStore } from 'pinia'
import { apiStrapi } from '@/helpers/api.js'
import { handleApiError } from '@/services/errorHandler'
import { getStartedStepsFromStrapi } from '@/helpers/getStarted'

// Every component field of a Getting Started step or page, and the help page each row may link to (by slug only —
// the pages are fetched on their own and joined in getStartedStepsFromStrapi).
const GET_STARTED_POPULATE = [
  'populate[videoImage]=true',
  'populate[guide][populate][page][fields][0]=slug',
  'populate[sections][populate][page][fields][0]=slug',
  'populate[guides][populate][steps]=true',
  'populate[guides][populate][page][fields][0]=slug',
  'populate[guides][populate][sections][populate][page][fields][0]=slug',
].join('&')

export const useStrapiStore = defineStore('Strapi', {
  state: () => {
    return {
      banner: {},
      changeLog: [],
      // The hub's What's new row holds its place while the posts load.
      changeLogLoading: false,
      coachingCards: [],
      resources: [],
      notificationStatus: {},
      videoTour: {},
      gettingStartedSteps: [],
      // The new Home and Help Hub's steps, from Strapi. Empty until they load, and the Get started surfaces
      // stay hidden while it is (useGetStarted).
      getStartedSteps: [],
      getStartedRequested: false,
      // A help page has nothing to show while the steps load or after they fail.
      getStartedLoading: false,
      getStartedFailed: false,

      feeds: [],
			feedDetails: null,
			// The open post's request: which id, in flight, failed; an unknown id leaves `feedDetails` null.
			feedDetailsId: null,
			feedDetailsLoading: false,
			feedDetailsFailed: false,
			feedsLoader: false,
			feedsFetching: false,
			feedsHasMore: true,
			// How many posts the CMS has, from the last page's meta.
			feedsTotal: 0
    }
  },
  actions: {
    async fetchAllStrapiData() {
      this.fetchGettingStartedSteps()
      this.fetchVideoTour()
      this.fetchBanner()
      this.fetchChangeLogData()
      this.fetchResources()
      this.fetchSystemStatusNotificationBar()
    },

    // Once per session: the content changes when an editor publishes, not while a dealer is using it.
    async fetchGetStartedSteps() {
      if (this.getStartedRequested) return

      this.getStartedRequested = true
      this.getStartedLoading = true
      this.getStartedFailed = false

      try {
        const [steps, pages] = await Promise.all([
          // Steps also carry the tasks their progress is counted by.
          apiStrapi('/app-help-hub-pages', { 'pagination[pageSize]': 100 }, `${GET_STARTED_POPULATE}&populate[tasks]=true`),
          apiStrapi('/get-started-pages', { 'pagination[pageSize]': 100 }, `${GET_STARTED_POPULATE}&populate[leadSections][populate][page][fields][0]=slug`),
        ])

        // A failed request still parses (an error body has no `data`), so it fails like one that throws.
        if (!Array.isArray(steps?.data) || !Array.isArray(pages?.data)) {
          this.getStartedRequested = false
          this.getStartedFailed = true
          return
        }

        // None published: the Get started surfaces stay hidden.
        if (!steps.data.length) return

        this.getStartedSteps = getStartedStepsFromStrapi(steps.data, pages.data)
      } catch (error) {
        // Retry, or a later mount, asks again.
        this.getStartedRequested = false
        this.getStartedFailed = true
        handleApiError(error, { component: 'StrapiStore', action: 'fetchGetStartedSteps' })
      } finally {
        this.getStartedLoading = false
      }
    },

    async fetchBanner() {
      try {
        const { data } = await apiStrapi(
          '/cdp-banners',
          {
            populate: '*',
            publicationState: 'live',
          },
          'pagination[limit]=1'
        )
        if (data.length === 0) {
          return
        }
        const { id } = data[0]
        const { description, headline, ctaLink, ctaLabel, image } =
          data[0].attributes

        this.banner = {
          description,
          headline,
          id,
          ctaLink,
          ctaLabel,
          imageUrl: `https://content.carddealerpro.com${image.data.attributes.url}`,
        }
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchBannerData' })
      }
    },

    async fetchChangeLogData() {
      this.changeLogLoading = true

      try {
        const { data } = await apiStrapi(
          '/cdp-changelog-posts',
          {
            sort: 'date:DESC',
          },
          'populate[0]=section,improvement&populate[1]=section.video,section.images&populate[2]=section.video.file,section.video.poster'
        )

        this.changeLog = data
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchChangeLogData' })
      } finally {
        this.changeLogLoading = false
      }
    },

    async fetchCoachingCards() {
      try {
        const { data } = await apiStrapi('/cdp-coaching-cards', {
          populate: '*',
        })

        if (data.length === 0) {
          this.coachingCards = []
          return
        }
        this.coachingCards = data
          .filter((card) => !card.attributes.showOnlyInDev)
          .map((card) => {
            const {
              title,
              headline,
              ctaLabel,
              ctaLink,
              description,
              coverImage,
              video,
              path,
              youtubeLink,
              priority,
            } = card.attributes
            const id = card.id
            return {
              id,
              title,
              headline,
              ctaLabel,
              ctaLink,
              description,
              path,
              youtubeLink,
              priority,
              coverImage: coverImage.data
                ? `https://content.carddealerpro.com${coverImage.data.attributes.url}`
                : {},
              video: video.data
                ? `https://content.carddealerpro.com${video.data.attributes.url}`
                : {},
            }
          })
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchCoachingCards' })
      }
    },

    async fetchGettingStartedSteps() {
      try {
        const { data } = await apiStrapi(
          '/Cdp-getting-started-steps',
          'Cdp-getting-started-steps?populate[0]=video&populate[1]=video.file, video.poster'
        )
        if (data.length === 0) {
          this.gettingStartedSteps = []
          return
        }
        this.gettingStartedSteps = data.map((step) => {
          const {
            name,
            title,
            done,
            description,
            video: videoComponent,
            route,
            routeText,
            canSkip,
          } = step.attributes
          const id = step.id
          const video = {}
          if (videoComponent) {
            video.src = `https://content.carddealerpro.com${videoComponent.file.data?.attributes?.url}`
            video.poster = `https://content.carddealerpro.com${videoComponent.poster.data?.attributes?.url}`
          }
          return {
            id,
            name,
            title,
            done,
            description,
            route,
            routeText,
            canSkip,
            video,
          }
        })
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchGettingStartedSteps' })
      }
    },

    async fetchResources() {
      try {
        const { data } = await apiStrapi(
          '/cdp-resources',
          {
            populate: '*',
          },
          'pagination[limit]=3&filters[featured]=true'
        )

        this.resources = data
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchResources' })
      }
    },

    async fetchVideoTour() {
      try {
        const { data } = await apiStrapi('/video-tour', {
          populate: '*',
        })
        const { coverImage, headline, video } = data.attributes

        this.videoTour = {
          headline,
          videoUrl: `https://content.carddealerpro.com${video.data.attributes.url}`,
          imageUrl: `https://content.carddealerpro.com${coverImage.data.attributes.url}`,
        }
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchVideoTour' })
      }
    },

    async fetchSystemStatusNotificationBar() {
      try {
        const { data } = await apiStrapi('/system-notification', {
          populate: '*',
        })
        if (!data) {
          this.notificationStatus = {}
          return
        }
        const { headline, message, display } = data.attributes

        if (!display) {
          this.notificationStatus = {}
          return
        }

        this.notificationStatus = {
          headline,
          message,
        }
      } catch (error) {
        handleApiError(error, { component: 'StrapiStore', action: 'fetchSystemStatusNotificationBar' })
      }
    },

		async fetchFeeds() {
			if (!this.feedsHasMore || this.feedsFetching) return

			this.feedsFetching = true
			// The loader is for an empty list; a further page shows as a placeholder at the end of it.
			if (!this.feeds.length) this.feedsLoader = true

			try {
				// Offset paging on a stable order, so a page never repeats or skips a post.
				const { data, meta } = await apiStrapi(
					'/blog-posts',
					{
						populate: '*',
						sort: 'date:DESC,id:DESC',
					},
					`pagination[limit]=25&pagination[start]=${this.feeds.length}`,
				)

				if (data) {
					this.feeds = [...this.feeds, ...data]
					this.feedsTotal = meta.pagination.total
					this.feedsHasMore = data.length > 0 && this.feeds.length < meta.pagination.total
				}
			} catch (error) {
				handleApiError(error, { component: 'StrapiStore', action: 'fetchFeeds' })
			} finally {
				this.feedsLoader = false
				this.feedsFetching = false
			}
		},

		// One post with everything its page renders; a later call for another post takes over, so a slow answer never replaces a newer one.
		async fetchFeedDetails(feedId) {
			this.feedDetails = null
			this.feedDetailsId = feedId
			this.feedDetailsLoading = true
			this.feedDetailsFailed = false

			try {
				const { data } = await apiStrapi('/blog-posts', {
					'filters[id][$eq]': feedId,
					'populate[0]': 'section.video,section.images',
					'populate[1]': 'section.video.file,section.video.poster',
					'populate[2]': 'coverImage,coverVideo',
					// The tag relation once the CMS has it, so the page and the list agree.
					'populate[3]': 'tag',
				})

				if (this.feedDetailsId !== feedId) return

				// A failed request still parses (an error body has no `data`), so it fails like one that throws.
				if (!Array.isArray(data)) {
					this.feedDetailsFailed = true
					return
				}

				this.feedDetails = data[0] ?? null
			} catch (error) {
				if (this.feedDetailsId === feedId) this.feedDetailsFailed = true
				handleApiError(error, { component: 'StrapiStore', action: 'fetchFeedDetails' })
			} finally {
				if (this.feedDetailsId === feedId) this.feedDetailsLoading = false
			}
		}
  },
})
